// FileField: the browser's own file input, drawn as the kit's recessed well holding a
// gold-tinted key. The browser's words stay ("No file chosen", the name, "3 files"), so it shows
// what is chosen with no help from the page. For plain uploads in a form; a photo from a phone
// is FilePick, a drop is DropZone. docs/operations/design-standard.md, "Controls".
//
// m-0 and p-0.5 are load-bearing: the global `input` rule in index.css sets margin auto and
// padding 0. The value is NOT cleared after a pick, because the browser's words are the display;
// a page that wants the same file to fire twice clears it through the ref when it is done.

import React from 'react';

const WELL = [
  'm-0 block w-full min-w-0 max-w-none cursor-pointer rounded-control border-0 bg-surface-container-high p-0.5',
  'text-on-surface-variant shadow-recess transition-[box-shadow,background-color] duration-quick ease-emphasized',
  'disabled:cursor-default disabled:bg-surface-container disabled:text-on-surface-disabled disabled:shadow-none',
  'focus:outline-none focus-visible:ring-2',
  // The key inside the well: the kit's active-in-a-track tint, gold words on gold at 15%.
  'file:mr-3 file:cursor-pointer file:rounded-inner file:border-0 file:bg-primary/15 file:px-3 file:font-medium file:text-primary-ink',
  'file:transition-colors file:duration-quick file:ease-emphasized hover:file:bg-primary/25',
  'disabled:file:cursor-default disabled:file:bg-transparent disabled:file:text-on-surface-disabled',
].join(' ');

const SIZE = {
  sm: 'h-7 text-xs file:h-6 file:text-xs',
  md: 'h-8 text-sm file:h-7 file:text-[13px]',
} as const;

export interface FileFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  size?: 'sm' | 'md';
  error?: boolean;
  /** The chosen files as an array, alongside the native onChange. Not called for an empty pick. */
  onFiles?: (files: File[]) => void;
}

export const FileField = React.forwardRef<HTMLInputElement, FileFieldProps>(
  ({ size = 'md', error, onFiles, onChange, className = '', ...rest }, ref) => (
    <input
      ref={ref}
      type="file"
      aria-invalid={error || undefined}
      {...rest}
      onChange={(e) => {
        onChange?.(e);
        const files = Array.from(e.currentTarget.files || []);
        if (onFiles && files.length) onFiles(files);
      }}
      className={`${WELL} ${SIZE[size]} ${error ? 'ring-2 ring-red-500/60 focus-visible:ring-red-500/80' : 'focus-visible:ring-primary/70'} ${className}`}
    />
  ),
);
FileField.displayName = 'FileField';

export default FileField;
